import { useEffect, useState } from "react";
import { Download, FileSpreadsheet, FileText, Loader2, Presentation, FileCode2, AlertCircle, Square } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { logWarn } from "@/lib/log";
import { useAuth } from "@/context/AuthContext";
import { stoppedToast } from "@/lib/aiJob";

const ICON = { pptx: Presentation, ppt: Presentation, xlsx: FileSpreadsheet, xls: FileSpreadsheet, csv: FileSpreadsheet, html: FileCode2, md: FileCode2 };
const size = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);

function useDocument(id) {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    let alive = true, timer;
    const load = () => http.get(`/documents/${id}`).then((r) => {
      if (!alive) return;
      setDoc(r.data);
      if (r.data.status === "pending") timer = setTimeout(load, 2500);
    }).catch((e) => logWarn("Document status failed", e));
    load();
    return () => { alive = false; clearTimeout(timer); };
  }, [id]);
  return [doc, setDoc];
}

async function download(doc) {
  try {
    const r = await http.get(`/documents/${doc.document_id}/download`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = doc.filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  } catch (e) { toast.error(formatError(e)); }
}

function Body({ doc }) {
  if (!doc || doc.status === "pending") return <p className="text-xs text-neutral-400 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Creating your document…</p>;
  if (doc.status === "canceled") return <p className="text-xs text-neutral-400" data-testid={`document-canceled-${doc.document_id}`}>{doc.error || "Stopped."}</p>;
  if (doc.status === "failed") return <p className="text-xs text-red-300 flex items-center gap-2"><AlertCircle className="h-3 w-3" /> {doc.error}</p>;
  return (
    <>
      <p className="text-sm text-neutral-100 truncate">{doc.filename}</p>
      <p className="text-[11px] text-neutral-500 uppercase">{doc.ext} · {size(doc.size)}</p>
      {doc.note && <p className="text-[11px] text-yellow-200/80 mt-1 normal-case" data-testid={`document-note-${doc.document_id}`}>{doc.note}</p>}
    </>
  );
}

// A stopped file is never offered for download; the charge follows how far the build got.
function StopDoc({ id, setDoc }) {
  const { refreshEnt } = useAuth();
  const stop = async () => {
    setDoc((d) => ({ ...d, status: "canceled", error: "Stopping…" }));
    try { const { data } = await http.post(`/documents/${id}/cancel`); setDoc(data); if (data.status === "canceled") stoppedToast(data.charged); }
    catch (e) { toast.error(formatError(e)); } finally { refreshEnt(); }
  };
  return (
    <button onClick={stop} data-testid={`document-stop-${id}`}
      className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border border-white/15 text-neutral-300 hover:bg-white/10 transition-colors shrink-0">
      <Square className="h-3 w-3 fill-current" /> Stop
    </button>
  );
}

export function DocumentCard({ id }) {
  const [doc, setDoc] = useDocument(id);
  const Icon = ICON[doc?.ext] || FileText;
  return (
    <div className="mt-4 flex items-center gap-3 p-3 rounded-2xl border border-white/10 bg-white/[0.04] max-w-md" data-testid={`document-card-${id}`}>
      <div className="h-10 w-10 rounded-xl bg-sky-400/10 text-sky-300 flex items-center justify-center shrink-0"><Icon className="h-5 w-5" /></div>
      <div className="min-w-0 flex-1"><Body doc={doc} /></div>
      {(!doc || doc.status === "pending") && <StopDoc id={id} setDoc={setDoc} />}
      {doc?.status === "done" && (
        <button onClick={() => download(doc)} data-testid={`document-download-${id}`}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full bg-sky-400 text-white hover:bg-sky-500 transition-colors shrink-0">
          <Download className="h-3.5 w-3.5" /> Download
        </button>
      )}
    </div>
  );
}
